"use client";

/**
 * ConversionTracker — client component that tracks clicks on CTAs
 * using data attributes (no need to make parent server components client-side).
 *
 * Place this once in the root layout. It listens for clicks on elements
 * with `data-track-*` attributes and fires the appropriate analytics events.
 *
 * Supported data attributes:
 *   data-track-event="click_install"        — event name
 *   data-track-platform="ios"               — extra property
 *   data-track-source="hero_cta"            — extra property
 *   data-track-content-type="itinerary"     — extra property
 *   data-track-content-id="paris-3-days"    — extra property
 *
 * Any `data-track-*` attribute (except `data-track-event`) is passed
 * as a camelCase property to the analytics event.
 */

import { useEffect } from "react";
import { analytics } from "@/lib/analytics";
import type { EventName } from "@/lib/analytics";
import {
  getPersistedUtmParams,
  utmToEventContext,
} from "@/lib/analytics/utm";

/** Convert kebab-case to camelCase */
function toCamelCase(str: string): string {
  return str.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}

export default function ConversionTracker() {
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = (e.target as HTMLElement).closest("[data-track-event]");
      if (!target) return;

      const el = target as HTMLElement;
      const eventName = el.dataset["trackEvent"] as EventName | undefined;
      if (!eventName) return;

      // Collect all data-track-* attributes as properties
      const properties: Record<string, unknown> = {};
      for (const attr of Array.from(el.attributes)) {
        if (
          attr.name.startsWith("data-track-") &&
          attr.name !== "data-track-event"
        ) {
          const key = toCamelCase(attr.name.replace("data-track-", ""));
          properties[key] = attr.value;
        }
      }

      // Enrich with UTM context
      const utmContext = utmToEventContext(getPersistedUtmParams());

      // Enrich with page context
      const pathname = window.location.pathname;
      const localeMatch = pathname.match(/^\/([a-z]{2})\//);

      const enriched = {
        ...properties,
        locale: localeMatch?.[1],
        referrer: document.referrer,
        ...utmContext,
      };

      analytics.track(
        eventName,
        enriched as Parameters<typeof analytics.track>[1],
      );
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  return null;
}
